import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { NotificationManager } from 'react-notifications';
import SquareButton from 'Components/UI/SquareButton';
import './index.css';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import SentryScopedError from 'Errors/SentryScopedError';
import relationships from 'facet-rest/src/consts/relationships';

class PlaidComponentError extends SentryScopedError {
  constructor(message, sentryScope, ...params) {
    super(sentryScope, ...params);

    this.name = 'PlaidComponentError';
    this.message = message;
  }
}

export const INVALID_LINK_TOKEN = 'INVALID_LINK_TOKEN';

function PlaidComponent({
  linkText,
  children,
  institutionResource,
  linkSuccessCallback,
  linkFailureCallback,
  onSyncStarted,
  onExit,
  onClick,
  plaidLink,
}) {
  const {
    householdResource,
  } = useSimpleHousehold();
  const [plaidOptions, setPlaidOptions] = useState({});

  const getPlaidLinkToUse = (instResource) => {
    let resource = householdResource;
    if (instResource) {
      resource = instResource;
    }
    let linkToUse = resource.withRelationship(
      relationships.plaidToken.name,
      (rel) => rel,
      () => null,
    );

    if (plaidLink) {
      linkToUse = plaidLink;
    }
    return linkToUse;
  };

  const plaidSuccess = async (publicToken, metadata) => {
    const m = metadata;
    m.progress = 0;
    const plaidLinkToUse = getPlaidLinkToUse();
    const tokenCollection = await plaidLinkToUse.get();
    const tokenResource = tokenCollection.create();
    tokenResource.attr('token', publicToken);

    try {
      if (onSyncStarted) {
        await onSyncStarted();
      }

      const coll = await tokenResource.submit({ linkArgs: { raw: true } });
      if (linkSuccessCallback) {
        await linkSuccessCallback(coll);
      }

      displaySuccess('Successfully synced accounts');
    } catch (e) {
      if (e.toString().includes('Timeout of 60000ms exceeded')) {
        NotificationManager.warning(`We are still working on syncing your ${metadata.institution.name} accounts.  Please check back in a few minutes`);
        return;
      }

      displayErrorMessage(e, 'Failed to sync account');
      if (linkFailureCallback) {
        await linkFailureCallback(e);
      }
    }
  };

  const getLinkToken = async () => {
    const plaidLinkToUse = getPlaidLinkToUse();
    const [plaidLinkResource] = await plaidLinkToUse.get();
    return plaidLinkResource.attr('token');
  };

  const initializePlaidLinkOptions = async () => {
    setPlaidOptions({
      token: await getLinkToken(),
      onSuccess: plaidSuccess,
      onLoad: () => {},
      onExit: async (err, metadata) => {
        onExit(err);

        if (err != null && err.error_code === INVALID_LINK_TOKEN) {
          window.Plaid.destroy();
          setPlaidOptions({
            ...plaidOptions,
            token: await getLinkToken(),
          });
        }

        // On cancel this is an empty error object, so check if type set before showing snackbar.
        if (err && err.type) {
          const { error_message, ...rest } = err; // eslint-disable-line camelcase
          // eslint-disable-next-line no-console
          console.error(err);
          displayErrorMessage(
            new PlaidComponentError(error_message, {
              ...rest,
              ...metadata,
            }),
            'Failed to sync account',
          );
        }
      },
      onEvent: async (eventName, metadata) => {
        if (eventName !== 'TRANSITION_VIEW' && eventName !== 'SEARCH_INSTITUTION') { // These are useless events, it just says what screen they are on.
          // Log the event to our plaidEvent table, wrap in a try catch, so we do not blow up on fail. This is a background process
          try {
            householdResource.relationship('plaidEvent').link('related').post({ attributes: { eventName, ...metadata } }, { deprecatedAllowAlterOfRelation: true });
          } catch (e) {
            // eslint-disable-next-line
            console.log('error logging plaid event', e);
          }
        }
      },
    });
  };

  useEffect(() => {
    initializePlaidLinkOptions()
      .catch((e) => {
        displayErrorMessage(e, 'Error creating plaid link token');
      });
  }, [householdResource]);

  async function interceptClick(event) {
    event.stopPropagation();
    onClick();
    if (!institutionResource) {
      window.Plaid.create(plaidOptions).open();
      return;
    }

    const plaidLinkToUse = getPlaidLinkToUse(institutionResource);
    const [tokenResource] = await plaidLinkToUse.get();

    const options = plaidOptions;
    options.token = tokenResource.attr('token');

    window.Plaid.create(options).open();

    setPlaidOptions({
      ...plaidOptions,
      token: null,
    });
  }

  return (
    <div
      role="presentation"
      className="plaid-link-container"
      onClick={interceptClick}
    >
      {children || (
        <SquareButton
          icon="plus"
          color="green"
          test="Import Account Now"
          text={linkText}
        />
      )}
    </div>
  );
}

PlaidComponent.propTypes = {
  linkText: PropTypes.string,
  children: PropTypes.node,
  institutionResource: PropTypes.object,
  linkSuccessCallback: PropTypes.func,
  linkFailureCallback: PropTypes.func,
  plaidLink: PropTypes.object,
  onExit: PropTypes.func,
  onClick: PropTypes.func,
  onSyncStarted: PropTypes.func,
};

PlaidComponent.defaultProps = {
  linkText: 'Link Accounts',
  plaidLink: null,
  children: undefined,
  institutionResource: null,
  linkSuccessCallback: () => {},
  linkFailureCallback: () => {},
  onExit: () => {},
  onClick: () => {},
  onSyncStarted: () => {},
};

export default PlaidComponent;
